import { AxiosError } from 'axios';

import type { ApiProblem, ApiProblemFieldError } from '@/types/api';

/** Error codes the SPA reacts to explicitly. Anything else falls into the generic bucket. */
export const ERROR_CODES = {
  validationError: 'validation_error',
  notFound: 'not_found',
  duplicateResource: 'duplicate_resource',
  concurrencyConflict: 'concurrency_conflict',
  forbidden: 'forbidden',
  invalidCredentials: 'invalid_credentials',
  unauthenticated: 'unauthenticated',
  tokenExpired: 'token_expired',
  accountLocked: 'account_locked',
  accountBanned: 'account_banned',
  refreshTokenInvalid: 'refresh_token_invalid',
  securityTokenInvalid: 'security_token_invalid',
  emailNotVerified: 'email_not_verified',
  apiKeyInvalid: 'api_key_invalid',
  domainRuleViolation: 'domain_rule_violation',
  promptQualityInsufficient: 'prompt_quality_insufficient',
  idempotencyConflict: 'idempotency_conflict',
  quotaExceeded: 'quota_exceeded',
  /**
   * Copying a Prompt Store entry into the library was refused for a reason of its own (TASK-0071).
   *
   * Named because the entry page says something different for each: a licence that grants no reuse is the author's decision,
   * while a priced entry is one the product cannot sell yet. A generic "it did not work" would leave a reader pressing the
   * same button again.
   */
  promptLicenceForbidden: 'prompt_licence_forbidden',
  promptPurchaseRequired: 'prompt_purchase_required',
  sharePasswordRequired: 'share_password_required',
  versionBodyNotRetained: 'version_body_not_retained',
  /**
   * A role request the account's membership already answers (TASK-0061).
   *
   * Named rather than left to `concurrency_conflict`: the answer is already the requested one — two administrators granted the
   * same role — so the screen re-reads the account instead of telling the operator that somebody was faster.
   */
  adminUserRoleUnchanged: 'admin_user_role_unchanged',
  /**
   * A public handle another account already holds (TASK-0060, TASK-0061).
   *
   * Named because the administration form attaches the sentence to the handle input: a page-level failure the operator cannot
   * act on would leave them looking at a field they have to change.
   */
  publicIdentifierTaken: 'public_identifier_taken',
  rateLimited: 'rate_limited',
  payloadTooLarge: 'payload_too_large',

  /**
   * Store purchases (TASK-0072).
   *
   * Four codes because four things can be wrong with a purchase and each has a different repair: the balance does not cover the
   * price (obtain credit — nothing was charged), an entry is no longer on sale or was already owned (remove the line and try
   * again), and the cart is empty (there is nothing to buy). Every one of them is a *refusal*, so nothing is debited and the cart
   * stays as it was.
   */
  storeInsufficientCredit: 'store_insufficient_credit',
  storeItemUnavailable: 'store_item_unavailable',
  storeItemAlreadyOwned: 'store_item_already_owned',
  storeCartEmpty: 'store_cart_empty',

  /**
   * Discount redemption (TASK-0073, ADR-0030).
   *
   * Seven codes, because a buyer's repair differs by case: a wrong code is retyped, an expired or not-yet-open one needs
   * another, a spent or withdrawn one is not coming back, a code already used needs a different code, and one assigned to
   * somebody else is not theirs to use. Each is a *refusal* — nothing is debited and the cart stays — which is what the cart
   * screen's message and the server's `4xx` agree on.
   */
  discountNotFound: 'discount_not_found',
  discountExpired: 'discount_expired',
  discountNotStarted: 'discount_not_started',
  discountExhausted: 'discount_exhausted',
  discountRevoked: 'discount_revoked',
  discountAlreadyUsed: 'discount_already_used',
  discountNotApplicable: 'discount_not_applicable',

  /**
   * A sign-in method the policy switched off (TASK-0063).
   *
   * Two codes because the two paths answer differently: the password form gets `local_login_disabled` (`401`) and the external flow gets
   * `external_login_unavailable` (`404`, deliberately indistinguishable from an unknown provider).
   */
  localLoginDisabled: 'local_login_disabled',
  externalLoginUnavailable: 'external_login_unavailable',
  /**
   * The organisation sign-in's failures, each with its own repair (TASK-0064 §11).
   *
   * `organisation_token_invalid` is a callback the backend could not verify; `organisation_username_missing` means the authority
   * released no usable business username and nothing was created; `organisation_username_taken` means the username belongs to an
   * existing account and support must resolve the collision; `organisation_provider_unavailable` is the temporary outage.
   */
  organisationTokenInvalid: 'organisation_token_invalid',
  organisationUsernameMissing: 'organisation_username_missing',
  organisationUsernameTaken: 'organisation_username_taken',
  organisationProviderUnavailable: 'organisation_provider_unavailable',
  internalError: 'internal_error',
  aiProviderError: 'ai_provider_error',
  aiProviderTimeout: 'ai_provider_timeout',
  aiModelMissing: 'ai_model_missing',
  /**
   * A reasoning model's answer that was cut off by its own completion ceiling (ADR-0016 §5).
   *
   * The co-pilot already retries such a call once with a larger ceiling; this code is what the user sees when even that
   * attempt was cut off, and it is named rather than left to the generic bucket because the repair is specific: pick a
   * model that does not think invisibly, or shorten the source.
   */
  aiResponseTruncated: 'ai_response_truncated',
  /**
   * Text-to-prompt flow (TASK-0010 §23).
   *
   * Six classes, each with the status the API documents: the three source refusals and the ambiguity are `400`, an
   * oversized source is `413`, a provider answer that is not the contract is `502`, and a stale `contextToken` is `409`.
   * They are named here rather than left to the generic bucket because each one has a different repair: edit the source,
   * choose a source, upload a smaller file, generate again, or retry against the provider.
   */
  generationSourceEmpty: 'generation_source_empty',
  generationSourceAmbiguous: 'generation_source_ambiguous',
  generationSourceUnsupported: 'generation_source_unsupported',
  generationSourceTooLarge: 'generation_source_too_large',
  generationResponseMalformed: 'generation_response_malformed',
  generationStale: 'generation_stale',
  planUpgradeRequired: 'plan_upgrade_required',
  serviceUnavailable: 'service_unavailable',
  /** Client-side code: the request never reached the API. */
  networkError: 'network_error',
  /** Client-side code: the answer could not be understood. */
  unexpectedError: 'unexpected_error',
} as const;

export type ErrorCode = (typeof ERROR_CODES)[keyof typeof ERROR_CODES];

/**
 * Failure surfaced by the API client.
 *
 * Wrapping the problem document in an `Error` keeps stack traces and `instanceof` checks working while the
 * original RFC 7807 payload stays available for field-level binding and code-based UI decisions.
 */
export class ApiError extends Error {
  /** The problem document exactly as the API sent it (or a synthesized one for transport failures). */
  readonly problem: ApiProblem;

  constructor(problem: ApiProblem) {
    super(problem.detail ?? problem.title);
    this.name = 'ApiError';
    this.problem = problem;
  }
}

/** Narrows an unknown thrown value to an {@link ApiError}. */
export function isApiError(value: unknown): value is ApiError {
  return value instanceof ApiError;
}

/** Builds the problem document for a request that never produced an HTTP response. */
export function buildTransportProblem(code: ErrorCode, status = 0): ApiProblem {
  return {
    status,
    code,
    title: code === ERROR_CODES.networkError ? 'network_error' : 'unexpected_error',
  };
}

/** Reads the stable problem code of an unknown failure, or `null` when the value is not an API failure. */
export function problemCodeOf(value: unknown): string | null {
  return isApiError(value) ? value.problem.code : null;
}

/** Returns the field-level failures of a problem document, always as an array. */
export function fieldErrorsOf(value: unknown): ApiProblemFieldError[] {
  return isApiError(value) ? (value.problem.errors ?? []) : [];
}

/**
 * Reads an integer extension member of a problem document.
 *
 * ASP.NET Core serializes `ProblemDetails.Extensions` inline next to `title` and `code`, which is how the API publishes
 * the value a conditional write must send next time: a `409 concurrency_conflict` from the wizard carries the current
 * `revision` of the draft (API surface §3.3). Reading it here means the editor can repair itself from the failure
 * instead of spending a second round trip to discover what changed.
 *
 * @param value Thrown value to inspect.
 * @param name Extension member name, for example `revision`.
 * @returns The integer when the problem carries it, otherwise `null`.
 */
export function integerExtensionOf(value: unknown, name: string): number | null {
  if (!isApiError(value)) {
    return null;
  }

  const candidate = (value.problem as unknown as Record<string, unknown>)[name];

  return typeof candidate === 'number' && Number.isInteger(candidate) ? candidate : null;
}

/**
 * Reads a string extension member of a problem document.
 *
 * The AI endpoints publish `upstreamDetail` next to `code` and `title`: the provider's own message, which is what turns
 * "the provider could not be reached" into an actionable diagnosis (TASK-0009). Reading it here keeps the SPA's handling
 * of an extension identical to the integer one above.
 *
 * @param value Thrown value to inspect.
 * @param name Extension member name, for example `upstreamDetail`.
 * @returns The string when the problem carries a non-empty one, otherwise `null`.
 */
export function stringExtensionOf(value: unknown, name: string): string | null {
  if (!isApiError(value)) {
    return null;
  }

  const candidate = (value.problem as unknown as Record<string, unknown>)[name];

  return typeof candidate === 'string' && candidate.trim().length > 0 ? candidate : null;
}

/** Number of seconds a `429` asks the client to wait, when the API supplied `Retry-After`. */
export function retryAfterSecondsOf(value: unknown): number | null {
  if (!isApiError(value)) {
    return null;
  }

  const { retryAfterSeconds } = value.problem;
  return typeof retryAfterSeconds === 'number' && retryAfterSeconds > 0 ? retryAfterSeconds : null;
}

/**
 * Translates any thrown value into the {@link ApiError} shape the application works with.
 *
 * A response body that is not a problem document (a proxy error page, a truncated body) is normalized into the
 * `unexpected_error` code so callers never have to defend against a missing `code`.
 */
export function toApiError(error: unknown): ApiError {
  if (isApiError(error)) {
    return error;
  }

  if (error instanceof AxiosError) {
    const payload = error.response?.data as Partial<ApiProblem> | undefined;

    if (payload && typeof payload.code === 'string' && typeof payload.title === 'string') {
      return new ApiError({
        ...(payload as ApiProblem),
        status: error.response?.status ?? payload.status ?? 0,
      });
    }

    return new ApiError(
      buildTransportProblem(
        error.response ? ERROR_CODES.unexpectedError : ERROR_CODES.networkError,
        error.response?.status ?? 0,
      ),
    );
  }

  return new ApiError(buildTransportProblem(ERROR_CODES.unexpectedError));
}

/** Convenience wrapper around {@link toApiError} for `catch (thrown)` blocks. */
export function toApiProblem(error: unknown): ApiProblem {
  return toApiError(error).problem;
}
